import { BCMSDefaultModel, BCMSRecord, useGlobalController, useModel, useParams, Workflow } from "@bcm-dev/bcms-vite";
import BillerGroupsPage from "compid/pages/BillerGroupsPage";
import { Bill, Biller, BillerGroup, Reminder } from "model";
import React from "react";

// Steps of the workflow
const steps = {
    "/billerGroups" : <BillerGroupsPage/>,
};

/**
 * @function BillerGroupsWorkflow
 * @returns {React.Component}
 * @description The main component for the reminder workflow
 */
export default function BillerGroupsWorkflow(props) {
    // Initalize a global controller instance
    const controller = useGlobalController();
    // Grab the billerID from the parameters
    const { billerID } = useParams();
    // Access model.
    const model = useModel();
    // Get the default value for the workflow
    const value = getDefault(controller, billerID, model);
    // Define the constructor function for forcing state through the 'force-workflow-state' event
    const constructor = record => {
        const recordNewBillerGroupAsObj = { ...record.newBillerGroup };
        const recordNewBillerGroup = BillerGroup.init(recordNewBillerGroupAsObj);
        const mergedNewBillerGroup = BCMSDefaultModel.mergeRecords(recordNewBillerGroup, value.newBillerGroup);
        const billerGroup = BillerGroup.init(mergedNewBillerGroup);
        const defaultBillerGroup = BCMSDefaultModel.billerGroup();
        const addedBillerGroups = record.billerGroups ?? [];

        // Objects are converted into class types, 
        // and the "Other billers" placeholder group 
        // (which doesnt exist in the model globally), is also handled as an exception here.
        const recordMovedBiller = record.movedBiller ?? {};
        const recordMovedBillerInstance = Biller.initByID(recordMovedBiller.billerID);
        const recordMovedBillerFromBillerGroup = record.movedBillerFromBillerGroup ?? {};
        const recordMovedBillerToBillerGroup = record.movedBillerToBillerGroup ?? {};
        const movedBillerProps = { movedBiller: recordMovedBillerInstance };
        const fromIsOtherBillerGroup = recordMovedBillerFromBillerGroup.otherBillers === true;
        const toIsOtherBillerGroup = recordMovedBillerToBillerGroup.otherBillers === true;
        const movedBillerFromBillerGroupInstance = fromIsOtherBillerGroup ? getOtherBillersGroup(value.billerGroups) : BillerGroup.initByID(recordMovedBillerFromBillerGroup.billerGroupID);
        const movedBillerToBillerGroupInstance = toIsOtherBillerGroup ? getOtherBillersGroup(value.billerGroups) : BillerGroup.initByID(recordMovedBillerToBillerGroup.billerGroupID);
        const movedBillerFromBillerGroupObj = { movedBillerFromBillerGroup: movedBillerFromBillerGroupInstance };
        const movedBillerToBillerGroupObj = { movedBillerToBillerGroup: movedBillerToBillerGroupInstance };

        // This filtering logic was handled when the use interacted with the ui,
        // but now it should also be handled through state change for captures.
        const recordBillerGroups = addedBillerGroups.reduce((acc, group) => {
            const newBillerGroupObj = { ...defaultBillerGroup.record, ...group };
            const newBillerGroup = BillerGroup.init(newBillerGroupObj);
            newBillerGroup.billers = [];

            for(let i = 0; i < group.billers.length; i++) {
                let biller = group.billers[i];
                newBillerGroup.billers.push(Biller.initByID(biller.billerID));

                // For this biller, remove it from other groups if its already in other groups.
                for(let j = 0; j < value.billerGroups.length; j++) {
                    let group = value.billerGroups[j];
                    group.billers = group.billers.filter(groupBiller => {
                        return groupBiller.id !== biller.billerID;
                    });
                }
            }
            
            acc.push(newBillerGroup);
            return acc;
        }, []);

        const newBillerGroupProps = { newBillerGroup: billerGroup };
        const billerGroupsProps = { billerGroups: recordBillerGroups.concat([ getOtherBillersGroup(value.billerGroups) ]) };

        // "otherProps" handles the different class types
        // the user may want to update in the state.
        const otherProps = { ...newBillerGroupProps,
            ...billerGroupsProps,
            ...movedBillerProps,
            ...movedBillerFromBillerGroupObj,
            ...movedBillerToBillerGroupObj };

        return { ...value, ...record, ...otherProps };
    }
    return <Workflow value={ value } steps={ Object.values(steps) } constructor={ constructor } />
}

/**
 * @function getOtherBillersGroup
 * @returns { BillerGroup }
 * @description Function to get the "Other billers" group only used 
 * in this workflow, not globally accessible.
 */
function getOtherBillersGroup(billers) {
    return billers.filter(biller => {
        return biller.otherBillers === true;
    })[0];
}

/**
 * @function getWorkflowSteps
 * @returns {OBJECT}
 * @description Hook to get the steps of the reminder workflow
 */
export const getWorkflowSteps = () => steps;

/**
 * @function getDefault
 * @param {BCMSGlobalController} controller - Global controller instance
 * @param {STRING} billIDs - The bill ids passed through the parameter
 * @returns {OBJECT}
 * @note The billIDs parameter expects ids formatted as "1,2,3" since thats how they are being passed from the useParams
 * @description Function to create the default value of the reminder workflow.
 */
export const getDefault = (controller, billerID, model) => {
    BCMSRecord.globalControllerFor([ Biller, Bill, Reminder, BillerGroup ], controller);
    var billerGroups = BillerGroup.initAll().reverse();
    var allBillers = Biller.initAll();
    BillerGroup.sortNameAscending(allBillers);

    // Remove billers that have not been added by the user.
    allBillers = allBillers.filter(biller => {
        return biller.addType === "";
    });
    
    // If biller groups exist, set a "billers" property.
    // Add billers to this group.
    if(billerGroups.length > 0) {
        billerGroups = billerGroups.map(billerGroup => {
            var groupBillers = allBillers;
            if(allBillers) {
                groupBillers = groupBillers.filter(biller => {
                    if(biller.billerGroup === undefined) {
                        return false;
                    }
                    return biller.billerGroup.id === billerGroup.id;
                })
                // Set billerGroup property for each biller.
                // The placeholder "Other billers" group will not be set here.
                .map(biller => {
                    biller.billerGroup = billerGroup;
                    return biller;
                });

                // Remove the billers that have been put into user created Biller Groups.
                // The billers left over will be put in the "Other billers" group.
                allBillers = allBillers.filter(biller => {
                    for(var i = 0; i < groupBillers.length; i++){
                        const groupBiller = groupBillers[i];
                        if(groupBiller.id === biller.id) {
                            return false;
                        }
                    }
                    return true;
                });
    
                billerGroup.billers = groupBillers;
            }

            return billerGroup;
        });
    }

    // Create a placeholder "Other billers" group called 
    // "defaultBillerGroup" for easier looping in the workflow. 
    // This group only exists on this page,
    // but no model record of it exists and it is not
    // globally set in the model of the demo.
    const defaultBillerGroup = BCMSDefaultModel.billerGroup();
    defaultBillerGroup.name = "Other billers";
    defaultBillerGroup.billers = allBillers;
    defaultBillerGroup.otherBillers = true;
    defaultBillerGroup.id = BillerGroup.nextID();
    billerGroups.push(defaultBillerGroup);

    // This is the new biller group that the user will modify and might create.
    const newBillerGroup = BCMSDefaultModel.billerGroup();
    newBillerGroup.id = BillerGroup.nextID();
    newBillerGroup.name = "";
    newBillerGroup.billers = [];

    return { 
        billerGroups: billerGroups,
        addedNewBillerGroup: false,
        addedNewBillerGroupName: "",
        didMoveBillerToBillerGroup: false,
        movedBiller: undefined,
        movedBillerFromBillerGroup: undefined,
        movedBillerToBillerGroup: undefined,
        newBillerGroup: newBillerGroup,
        deletedBillerGroup: false,
        deletedBillerGroupName: false,
        // an array of BillerGroupEditItems
        billerGroupEditItems: [],
        didEditBillerGroup: false,
        currBillerGroupEditItem: undefined,
        editedBillerGroupName: ""
    };
};